import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as R from 'ramda'
import {
  List as ListIcon,
  LoveList as LoveListIcon,
} from '@pbt/pbt-ui-components/src/icons'

import FeatureToggle from '~/constants/featureToggle'
import { OrderType } from '~/constants/SOAPStates'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getOrdersSearchResults,
  getOrdersSearchResultsTotalCount,
} from '~/store/reducers/orders'
import { ListType, Order } from '~/types'

import {
  deleteItemByPrice,
  mergeItemsWithQuantitySum,
} from '../../invoices/invoiceUtils'
import OrderListWithFilters from './OrderListWithFilters'
import SelectableListWithFilters from './SelectableListWithFilters'

const defaultGetUniqId = R.path(['price', 'id'])
const defaultAddItem = (oldItems: Order[], newItems: Order[]) =>
  mergeItemsWithQuantitySum(oldItems, newItems, defaultGetUniqId)

const getAllowDecimalQuantity = (item: Order) =>
  item.type !== OrderType.LAB_TEST

interface OrderSelectableListWithFiltersProps {
  [key: string]: any
  addItem?: (oldItems: Order[], newItems: Order[]) => Order[]
  deleteItem?: (oldItems: Order[], newItems: Order[]) => Order[]
  enableFullFilter?: boolean
  getUniqId?: (obj: any) => any
  listTypes?: ListType[]
}

const OrderSelectableListWithFilters = forwardRef(
  function OrderSelectableListWithFilters(
    {
      listTypes,
      getUniqId = defaultGetUniqId,
      addItem = defaultAddItem,
      deleteItem = deleteItemByPrice,
      enableFullFilter,
      ...rest
    }: OrderSelectableListWithFiltersProps,
    ref,
  ) {
    const searchResults = useSelector(getOrdersSearchResults)
    const searchResultsTotalCount =
      useSelector(getOrdersSearchResultsTotalCount) || 0
    const { t } = useTranslation('Common')
    const includeOnHandQuantity = useSelector(
      getFeatureToggle(FeatureToggle.ON_HAND_INVENTORY_AWARENESS),
    )

    const OrderListTypes = [
      {
        id: 'OUR',
        name: t('Common:OUR_LIST'),
        isDefault: true,
        Icon: LoveListIcon,
      },
      {
        id: 'FULL',
        name: t('Common:FULL_LIST'),
        Icon: ListIcon,
      },
    ].filter(Boolean)

    return (
      <SelectableListWithFilters
        ListWithFiltersComponent={OrderListWithFilters}
        addItem={addItem}
        deleteItem={deleteItem}
        getAllowDecimalQuantity={getAllowDecimalQuantity}
        getUniqId={getUniqId}
        includeOnHandQuantity={includeOnHandQuantity}
        listTypes={listTypes || OrderListTypes}
        ref={ref}
        searchResults={searchResults}
        searchResultsTotalCount={searchResultsTotalCount}
        {...rest}
      />
    )
  },
)

export default OrderSelectableListWithFilters
